import {
  TableColumn as TableColumnWSR,
  TableColumn as WSRTableColumn,
} from '@wix/design-system/dist/types/Table';
import { NestedTableState } from '../../state';
import React from 'react';
import { useCreateWSRColumnBase } from '../../hooks/useCreateWSRColumnBase';
import { NestedTableFlatKeyedItem } from '../../state/NestedTableState/NestedTableFlatModeSectionState';
import { NestedTableFlatModeMainCell } from './NestedTableFlatModeMainCell';
import { useNestedTableActionsCell } from './useNestedTableActionsCell';

export interface UseFlatModeColumnsProps<C extends string> {
  state: NestedTableState<C>;
  horizontalScroll?: boolean;
  actionsCellWidth?: string | number;
  actionsCellProps?: Partial<Omit<TableColumnWSR<any>, 'render' | 'style'>>;
}

export function useFlatModeColumns<C extends string>(
  props: UseFlatModeColumnsProps<C>,
): WSRTableColumn<NestedTableFlatKeyedItem<C, any, any>>[] {
  const { state, actionsCellProps, actionsCellWidth, horizontalScroll } = props;

  const { toolbar } = state;

  const { selectedOrderedColumnsOrAll } = toolbar;

  const createWSRColumnBase = useCreateWSRColumnBase({ horizontalScroll });

  const actionsCellColumn = useNestedTableActionsCell({
    state: state.flat,
    actionsCellWidth,
    actionsCellProps,
  });

  return [
    {
      key: 'flat-mode-main-column',
      dataHook: 'flat-mode-main-column',
      title: '',
      width: '326px',
      ...state.mainColumn,
      style: undefined,
      render: ({ item }: NestedTableFlatKeyedItem<C, any, any>, rowNum) => (
        <NestedTableFlatModeMainCell item={item} rowNum={rowNum} />
      ),
    },
    ...selectedOrderedColumnsOrAll.map((column, index, { length }) => {
      const {
        id,
        title,
        sortable,
        infoTooltipProps,
        render,
        width,
        ...restColumn
      } = column;
      return {
        ...restColumn,
        ...createWSRColumnBase(column, index, length),
        sortDescending:
          sortable && id ? state.query.sort.getIsSortDescending(id) : undefined,
        render: (
          { item }: NestedTableFlatKeyedItem<any, any, any>,
          rowNum: number,
        ) => {
          return item.state.columns?.[id]?.(
            item.originalKeyedItem.item,
            rowNum,
          );
        },
      };
    }),
    ...(actionsCellColumn ? [actionsCellColumn] : []),
  ];
}
